import {Card, DataStatus, Separator} from 'platform/components';
import {Grid, GridItem, Heading, Box} from 'platform/foundation';
import {css} from 'styled-components';

import {isNil, isNotEmpty} from 'ramda';

import {useGetActiveBusinessCasesByVehicleQuery, useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {BusinessCaseSummary, DmsDataGrid, Main} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

export const VehicleBusinessCases = () => {
  const navigate = useNavigate();
  const {id: vehicleId} = useRequiredParams();

  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const {
    data: activeBusinessCases,
    isLoading,
    isError,
  } = useGetActiveBusinessCasesByVehicleQuery(
    {vehicleId: vehicle?.id ?? ''},
    {skip: isNil(vehicle)}
  );

  const queryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    vehicle: [vehicle?.id as string],
  });

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    switch (actionKey) {
      case 'detail':
        if (typeof rowId === 'string') {
          navigate(
            composePath(businessCaseRoutes.overview, {
              params: {id: rowId},
              isSearchParamsPreserved: false,
            })
          );
        }
        break;
    }
  };

  return (
    <Main>
      <Grid columns={1}>
        <DataStatus isLoading={isLoading} isError={isError}>
          <GridItem>
            {isNotEmpty(activeBusinessCases) ? (
              <Box paddingTop={1}>
                <Heading size={3} alternative>
                  {i18n.t('page.vehicle.activeBusinessCases.title')}
                </Heading>
              </Box>
            ) : (
              <div
                css={css`
                  margin-top: 4px;
                  margin-bottom: -32px;
                `}
              >
                <Heading size={3} alternative>
                  {i18n.t('page.vehicle.labels.noActiveBusinessCases')}
                </Heading>
                <Separator />
              </div>
            )}
          </GridItem>
          {activeBusinessCases?.map((activeBusinessCase, index) => (
            <BusinessCaseSummary
              key={activeBusinessCase.id}
              activeBusinessCase={activeBusinessCase}
              variant="customer"
              data-testid={testIds.vehicles.businessCases(`businessCaseSummary-${index}`)}
            />
          ))}
        </DataStatus>

        <Box paddingTop={3}>
          <Heading size={3} alternative>
            {i18n.t('page.vehicle.previousBusinessCases.title')}
          </Heading>
        </Box>
        <Card variant="inlineWhite">
          <Box height={100}>
            <DmsDataGrid
              gridCode="vehicle-business-case-history"
              actionCallback={actionCallback}
              queryModifier={queryModifier}
              data-testid={testIds.vehicles.businessCases('businessCaseHistory')}
            />
          </Box>
        </Card>
      </Grid>
    </Main>
  );
};
